import { SignIn, useAuth } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router";
import { useEffect } from "react";

export default function SignInPage() {
  const { userId, isLoaded } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user is already signed in, redirect to home
    if (isLoaded && userId) {
      navigate("/");
    }
  }, [isLoaded, userId, navigate]);

  // Don't render SignIn component if user is already signed in
  if (!isLoaded || userId) {
    return null;
  }

  return (
    <div className="w-full min-h-screen flex flex-col lg:flex-row gap-8 lg:gap-12 p-4 lg:p-8">
      {/* Left side - Sign in form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center lg:items-end">
        <div className="w-full max-w-md">
          <div className="text-center lg:text-right mb-6">
            <p className="font-ital text-2xl lg:text-3xl mb-2 text-center">zero_carbs</p>
            <p className="text-sm text-muted-foreground text-center">
              because spreadsheets can eat shit and die
            </p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <SignIn signUpUrl="/sign-up" />
          </div>
        </div>
      </div>

      {/* Right side - Information */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center lg:items-start">
        <div className="w-full max-w-xl space-y-8">
          {/* Developer's Note */}
          <div className="space-y-3">
            <h2 className="font-ital text-xl lg:text-2xl text-primary">
              Why I built this
            </h2>
            <p className="text-sm lg:text-base leading-relaxed">
              I built zero_carbs because I was tired of juggling spreadsheets,
              platform dashboards, and mental math just to know if I was
              actually making money reselling. I needed something simple that
              just... worked.
            </p>
          </div>

          {/* Problem/Solution */}
          <div className="space-y-4 p-4 lg:p-6 bg-muted/30 rounded-lg border border-border/50">
            <div>
              <p className="text-xs lg:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                The Problem
              </p>
              <p className="text-sm lg:text-base">
                Spreadsheets are tedious (and ugly). Platform dashboards don't talk to each
                other. Profit calculations required too much "spreadsheet logic".
              </p>
            </div>
            <div>
              <p className="text-xs lg:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                The Solution
              </p>
              <p className="text-sm lg:text-base">
                One place to track everything. Automatic analytics. Know your
                numbers.
              </p>
            </div>
          </div>

          {/* Start Free Callout */}
          <div className="space-y-3 p-4 lg:p-6 bg-primary/5 rounded-lg border-2 border-primary/20">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <h3 className="font-ital text-lg lg:text-xl text-primary">
                Start free - no credit card needed
              </h3>
            </div>
            <ul className="space-y-2 text-sm lg:text-base ml-7">
              <li>• Track up to 5 purchases</li>
              <li>• Manage up to 5 listings</li>
              <li>• Link up to 20 items</li>
              <li>• Full access to analytics & mobile app</li>
            </ul>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📦</span>
              <div>
                <p className="font-semibold text-sm lg:text-base">
                  Track purchases & listings
                </p>
                <p className="text-xs lg:text-sm text-muted-foreground">
                  All in one place
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">📊</span>
              <div>
                <p className="font-semibold text-sm lg:text-base">
                  Real-time analytics
                </p>
                <p className="text-xs lg:text-sm text-muted-foreground">
                  See your stats instantly
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">📱</span>
              <div>
                <p className="font-semibold text-sm lg:text-base">
                  Works on mobile
                </p>
                <p className="text-xs lg:text-sm text-muted-foreground">
                  Android app available (sort of)
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">💰</span>
              <div>
                <p className="font-semibold text-sm lg:text-base">
                  Actual profit tracking
                </p>
                <p className="text-xs lg:text-sm text-muted-foreground">
                  Not just sales
                </p>
              </div>
            </div>
          </div>

          {/* Trust Signals */}
          <div className="space-y-2 text-sm lg:text-base text-muted-foreground border-t border-border/50 pt-6">
            <p>✨ Built by a reseller, for resellers</p>
            <p>🔒 Your data is yours - we don't sell or share anything</p>
            <p>💻 Fully open source - check out the code on <Link className="underline hover:no-underline" to="https://github.com/zero-carbs" target="_blank" rel="noopener noreferrer">GitHub</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
